import type { DocMode } from '@blocksuite/affine/model';
import type { DocMeta } from '@blocksuite/affine/store';
import {
  Store,
  yjsGetPath,
  yjsObserve,
  yjsObserveDeep,
  yjsObservePath,
} from '@toeverything/infra';
import { nanoid } from 'nanoid';
import {
  combineLatest,
  distinctUntilChanged,
  map,
  type Observable,
  of,
  switchMap,
} from 'rxjs';
import { Array as YArray, Map as YMap, transact } from 'yjs';

import { isBackendKnownUnavailable } from '../../../utils/backend-availability';
import type { PermissionSnapshotStore } from '../../permissions/stores/permission-snapshot';
import type { TagStore } from '../../tag/stores/tag';
import type { WorkspaceService } from '../../workspace';
import type { DocMutationService } from '../services/doc-mutation-service';
import type { AccessibleDocCacheStore } from './accessible-doc-cache';
import type { DocPropertiesStore } from './doc-properties';

const INITIAL_TARGET_FOLDER_ID_META_KEY = 'streamlain:initialTargetFolderId';

/**
 * DocsStore — Sidebar data source for doc list and metadata.
 *
 * Data source selection by workspace type (NOT by role):
 * - Cloud workspace: GraphQL via AccessibleDocCacheStore (all roles, permission-filtered)
 * - Local workspace: Y.js rootYDoc.meta.pages (no server, no permissions)
 */
export class DocsStore extends Store {
  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly docPropertiesStore: DocPropertiesStore,
    private readonly accessibleDocCache: AccessibleDocCacheStore,
    private readonly tagStore: TagStore,
    private readonly docMutationService?: DocMutationService,
    private readonly permissionSnapshotStore?: PermissionSnapshotStore
  ) {
    super();
    const permanentDeleteSubscription =
      this.accessibleDocCache.permanentlyDeletedDocIds$.subscribe(docIds => {
        if (docIds.length === 0) return;
        void Promise.allSettled(
          docIds.map(docId => this.evictPermanentlyDeletedLocalDoc(docId))
        ).finally(() => {
          this.accessibleDocCache.permanentlyDeletedDocIds$.setValue([]);
        });
      });
    this.disposables.push(() => permanentDeleteSubscription.unsubscribe());
  }

  private async evictPermanentlyDeletedLocalDoc(docId: string): Promise<void> {
    try {
      await this.removeLocalDocState(docId);
    } catch (error) {
      console.error(
        `Failed to evict permanently deleted local doc ${docId}:`,
        error
      );
    }
  }

  /**
   * Whether the meta.setDocMeta interceptor has been installed.
   * @see installMetaInterceptor
   */
  private metaInterceptorInstalled = false;
  private readonly docTrashMutationVersions = new Map<string, number>();

  private get isCloudWorkspace(): boolean {
    return this.workspaceService.workspace.flavour !== 'local';
  }

  /**
   * Get a BlockSuite doc store by ID.
   *
   * sl-oiya.2.1: Permission-aware registration. The AccessibleDocBridge
   * bulk-registers docs from the GraphQL cache, but runs in useEffect
   * (async). This method can be called from useLayoutEffect (sync) before
   * the Bridge fires. If the doc isn't in DocCollection yet, register it
   * on-demand IF the AccessibleDocCacheStore confirms access (RBAC-safe).
   *
   * Local workspaces: docs come from Y.js root doc meta.pages, no cache check.
   */
  getBlockSuiteDoc(id: string) {
    const collection = this.workspaceService.workspace.docCollection;
    let doc = collection.getDoc(id);

    if (!doc && this.isCloudWorkspace) {
      // Bridge hasn't registered this doc yet (useEffect timing).
      // Only register if the permission cache confirms access.
      const cached = this.accessibleDocCache.getCachedDoc(id);
      if (cached) {
        const impl = collection as unknown as {
          registerDoc?: (docId: string) => void;
        };
        if (impl.registerDoc) {
          impl.registerDoc(id);
          doc = collection.getDoc(id);
        }
      }
    }

    return doc?.getStore({ id }) ?? null;
  }

  getBlocksuiteCollection() {
    return this.workspaceService.workspace.docCollection;
  }

  createDoc(docId?: string, initialTargetFolderId?: string) {
    const id = docId ?? nanoid();

    if (this.isCloudWorkspace) {
      const collection = this.workspaceService.workspace.docCollection;
      const impl = collection as unknown as {
        registerDoc?: (docId: string) => void;
      };
      impl.registerDoc?.(id);
      if (initialTargetFolderId) {
        // Seed placement before getBlockSuiteDoc() creates the Store/provider.
        // Otherwise the provider can publish an empty first update, which the
        // server links to the workspace root before initialized content carries
        // targetFolderId in a later update.
        collection
          .getDoc(id)
          ?.spaceDoc.getMap('meta')
          .set(INITIAL_TARGET_FOLDER_ID_META_KEY, initialTargetFolderId);
      }
      return id;
    }

    transact(
      this.workspaceService.workspace.rootYDoc,
      () => {
        const meta = this.workspaceService.workspace.rootYDoc.getMap('meta');
        let docs = meta.get('pages');
        if (docs === undefined) {
          docs = new YArray<YMap<unknown>>();
          meta.set('pages', docs);
        }
        if (!(docs instanceof YArray)) {
          throw new Error('Local workspace meta.pages must be a Y.Array');
        }

        docs.push([
          new YMap([
            ['id', id],
            ['title', ''],
            ['createDate', Date.now()],
            ['tags', new YArray()],
          ]),
        ]);
      },
      { force: true }
    );

    return id;
  }

  markDocCreating(id: string): void {
    if (this.isCloudWorkspace) {
      this.accessibleDocCache.markLocalDocCreating(id);
    }
  }

  /**
   * Notify the permission cache that a doc was created locally.
   * MUST be called AFTER the doc's block tree is fully initialized
   * (via initDocFromProps) to avoid a race condition where React
   * re-renders before blocks exist, causing DocImpl.load() to set
   * _ready=true without an initFn.
   */
  notifyDocCreated(id: string, initialMeta?: Partial<DocMeta>) {
    if (this.isCloudWorkspace) {
      this.accessibleDocCache.addLocalDoc(id, initialMeta);
      if (
        typeof this.permissionSnapshotStore?.applyPendingLocalOwner ===
        'function'
      ) {
        this.permissionSnapshotStore.applyPendingLocalOwner(
          id,
          `local-doc-create:${id}`
        );
      }
      this.accessibleDocCache.fetchAccessibleDocs().catch(() => {});
    }
    if (initialMeta && Object.keys(initialMeta).length > 0) {
      this.setDocMeta(id, initialMeta);
    }
  }

  // ─── Doc ID lists ────────────────────────────────────────────

  watchDocIds(): Observable<string[]> {
    if (this.isCloudWorkspace) {
      this.installMetaInterceptor();
      return this.accessibleDocCache.watchAccessibleDocIds();
    }
    return this._yjsDocIds();
  }

  watchNonTrashDocIds(): Observable<string[]> {
    if (this.isCloudWorkspace) {
      return this.accessibleDocCache
        .watchAllAccessibleDocs()
        .pipe(map(docs => docs.filter(d => !d.trashed).map(d => d.id)));
    }
    return this._yjsNonTrashDocIds();
  }

  watchTrashDocIds(): Observable<string[]> {
    if (this.isCloudWorkspace) {
      return this.accessibleDocCache
        .watchAllAccessibleDocs()
        .pipe(map(docs => docs.filter(d => d.trashed).map(d => d.id)));
    }
    return this._yjsTrashDocIds();
  }

  // ─── Doc metadata lists ──────────────────────────────────────

  watchAllDocTitle(): Observable<Array<{ id: string; title: string }>> {
    if (this.isCloudWorkspace) {
      return this.accessibleDocCache
        .watchAllAccessibleDocs()
        .pipe(map(docs => docs.map(d => ({ id: d.id, title: d.title }))));
    }
    return this._yjsAllDocTitle();
  }

  watchAllDocCreateDate(): Observable<
    Array<{ id: string; createDate: number }>
  > {
    if (this.isCloudWorkspace) {
      return this.accessibleDocCache
        .watchAllAccessibleDocs()
        .pipe(
          map(docs => docs.map(d => ({ id: d.id, createDate: d.createDate })))
        );
    }
    return this._yjsAllDocCreateDate();
  }

  watchAllDocUpdatedDate(): Observable<
    Array<{ id: string; updatedDate: number | undefined }>
  > {
    if (this.isCloudWorkspace) {
      return this.accessibleDocCache.watchAllAccessibleDocs().pipe(
        map(docs =>
          docs.map(d => ({
            id: d.id,
            updatedDate: d.updatedDate as number | undefined,
          }))
        )
      );
    }
    return this._yjsAllDocUpdatedDate();
  }

  watchAllDocTagIds() {
    // sl-xbxb: Delegate to TagStore which handles dual-path
    // (Y.js for local, GraphQL for cloud workspaces)
    return this.tagStore.watchAllDocTagIds();
  }

  watchDocMeta(id: string): Observable<Partial<DocMeta>> {
    if (this.isCloudWorkspace) {
      // Cache is the single source of truth for cloud workspaces.
      // installMetaInterceptor() ensures ALL callers of meta.setDocMeta()
      // (including BlockSuite's doc-title.ts) propagate to the cache.
      return this.accessibleDocCache.watchAccessibleDocMeta(id).pipe(
        map(gqlMeta => {
          if (!gqlMeta) return {};
          return {
            id: gqlMeta.id,
            title: gqlMeta.title ?? '',
            trash: gqlMeta.trashed,
            createDate: gqlMeta.createDate,
            updatedDate: gqlMeta.updatedDate,
          } as Partial<DocMeta>;
        })
      );
    }
    return this._yjsDocMeta(id);
  }

  watchDocIsDemo(id: string): Observable<boolean> {
    if (this.isCloudWorkspace) {
      return this.accessibleDocCache.watchAccessibleDocMeta(id).pipe(
        map(meta => meta?.isDemo === true),
        distinctUntilChanged()
      );
    }
    return this.docPropertiesStore.watchDocProperties(id).pipe(
      map(properties => properties.isDemo === 'true'),
      distinctUntilChanged()
    );
  }

  watchDemoDocIds(): Observable<string[]> {
    if (this.isCloudWorkspace) {
      return this.accessibleDocCache.watchAllAccessibleDocs().pipe(
        map(docs => docs.filter(doc => doc.isDemo === true).map(doc => doc.id)),
        distinctUntilChanged(
          (previous, current) =>
            previous.length === current.length &&
            previous.every((id, index) => id === current[index])
        )
      );
    }
    return this.watchDocIds().pipe(
      switchMap(ids =>
        ids.length === 0
          ? of([])
          : combineLatest(ids.map(id => this.watchDocIsDemo(id))).pipe(
              map(flags => ids.filter((_, index) => flags[index]))
            )
      ),
      distinctUntilChanged(
        (previous, current) =>
          previous.length === current.length &&
          previous.every((id, index) => id === current[index])
      )
    );
  }

  /**
   * Check if a doc was created locally in this session (creator = current user).
   * Used to bypass async permission checks for self-created docs.
   */
  isLocallyCreated(docId: string): boolean {
    return (
      this.isCloudWorkspace && this.accessibleDocCache.isLocallyCreated(docId)
    );
  }

  async discardLocalDoc(docId: string): Promise<void> {
    await this.removeLocalDocState(docId);
  }

  private async removeLocalDocState(docId: string): Promise<void> {
    const workspace = this.workspaceService.workspace;
    workspace.docCollection.removeDoc(docId);
    const resetLocalDoc = workspace.engine.doc.storage.resetLocalDoc;
    if (!resetLocalDoc) {
      throw new Error('Local doc reset is not available');
    }
    await resetLocalDoc.call(workspace.engine.doc.storage, docId);
    if (this.isCloudWorkspace) {
      this.accessibleDocCache.discardLocalDoc(docId);
      if (
        typeof this.permissionSnapshotStore?.rejectPendingLocalOwner ===
        'function'
      ) {
        await this.permissionSnapshotStore.rejectPendingLocalOwner(
          docId,
          `local-doc-create:${docId}`
        );
      }
    }
  }

  async waitForServerConfirmed(
    docId: string,
    options: { timeoutMs?: number; pollMs?: number } = {}
  ): Promise<boolean> {
    if (!this.isCloudWorkspace) return true;
    return this.accessibleDocCache.waitForServerConfirmed(docId, options);
  }

  async ensureDocCached(
    docId: string,
    options: { signal?: AbortSignal } = {}
  ): Promise<boolean> {
    if (!this.isCloudWorkspace) return true;
    return this.accessibleDocCache.ensureDocCached(docId, options.signal);
  }

  // ─── Sync & mode (unchanged — Y.js based for doc content) ───

  watchDocListReady() {
    if (this.isCloudWorkspace) {
      // Cloud workspaces use GraphQL-based doc list (sl-jsx3.5).
      // Ready once the permission cache has data (from IDB, server, or addLocalDoc).
      // Must NOT wait for Y.js root-doc sync — that blocks locally created docs.
      return this.accessibleDocCache.isPopulated$;
    }
    // Local workspaces: Y.js root-doc must be synced to know what docs exist.
    return this.workspaceService.workspace.engine.doc
      .docState$(this.workspaceService.workspace.id)
      .pipe(map(state => (state as { synced: boolean }).synced));
  }

  setDocMeta(id: string, meta: Partial<DocMeta>): boolean {
    if (
      this.isCloudWorkspace &&
      meta.trash !== undefined &&
      isBackendKnownUnavailable()
    ) {
      if (meta.trash === true && this.isLocallyCreated(id)) {
        this.discardLocalDoc(id).catch(error => {
          console.error(`Failed to discard local doc ${id}:`, error);
        });
        return true;
      }
      return false;
    }

    const trashRollback =
      this.isCloudWorkspace &&
      meta.trash !== undefined &&
      this.docMutationService
        ? {
            snapshot: this.accessibleDocCache.captureTrashSnapshot([id]),
            version: this.nextDocTrashMutationVersion(id),
          }
        : null;
    this.workspaceService.workspace.docCollection.meta.setDocMeta(id, meta);
    if (this.isCloudWorkspace) {
      this.updateAccessibleDocCacheFromMeta(id, meta, trashRollback);
      const collection = this.workspaceService.workspace
        .docCollection as unknown as {
        registerDoc?: (docId: string, meta?: Partial<DocMeta>) => void;
      };
      collection.registerDoc?.(id, meta);
    }
    return true;
  }

  confirmDocTitlePersisted(id: string, title: string): void {
    this.accessibleDocCache.confirmLocalTitleWrite(id, title);
  }

  setDocPrimaryModeSetting(id: string, mode: DocMode) {
    return this.docPropertiesStore.updateDocProperties(id, {
      primaryMode: mode,
    });
  }

  getDocPrimaryModeSetting(id: string) {
    return this.docPropertiesStore.getDocProperties(id)?.primaryMode;
  }

  watchDocPrimaryModeSetting(id: string) {
    return this.docPropertiesStore.watchDocProperties(id).pipe(
      map(config => config?.primaryMode),
      distinctUntilChanged((p, c) => p === c)
    );
  }

  waitForDocLoadReady(id: string) {
    return this.workspaceService.workspace.engine.doc.waitForDocLoaded(id);
  }

  addPriorityLoad(id: string, priority: number) {
    return this.workspaceService.workspace.engine.doc.addPriority(id, priority);
  }

  // ─── Y.js data source (local workspaces only) ───────────────

  private _yjsDocIds() {
    return yjsGetPath(
      this.workspaceService.workspace.rootYDoc.getMap('meta'),
      'pages'
    ).pipe(
      switchMap(yjsObserve),
      map(meta => {
        if (meta instanceof YArray) {
          return meta.map(v => v.get('id') as string);
        } else {
          return [];
        }
      })
    );
  }

  private _yjsNonTrashDocIds() {
    return yjsGetPath(
      this.workspaceService.workspace.rootYDoc.getMap('meta'),
      'pages'
    ).pipe(
      switchMap(pages => yjsObservePath(pages, '*.trash')),
      map(meta => {
        if (meta instanceof YArray) {
          return meta
            .map(v => (v.get('trash') ? null : v.get('id')))
            .filter(Boolean) as string[];
        } else {
          return [];
        }
      })
    );
  }

  private _yjsTrashDocIds() {
    return yjsGetPath(
      this.workspaceService.workspace.rootYDoc.getMap('meta'),
      'pages'
    ).pipe(
      switchMap(pages => yjsObservePath(pages, '*.trash')),
      map(meta => {
        if (meta instanceof YArray) {
          return meta
            .map(v => (v.get('trash') ? v.get('id') : null))
            .filter(Boolean) as string[];
        } else {
          return [];
        }
      })
    );
  }

  private _yjsAllDocTitle() {
    return yjsGetPath(
      this.workspaceService.workspace.rootYDoc.getMap('meta'),
      'pages'
    ).pipe(
      switchMap(pages => yjsObservePath(pages, '*.title')),
      map(pages => {
        if (pages instanceof YArray) {
          return pages.map(v => ({
            id: v.get('id') as string,
            title: (v.get('title') ?? '') as string,
          }));
        } else {
          return [];
        }
      })
    );
  }

  private _yjsAllDocCreateDate() {
    return yjsGetPath(
      this.workspaceService.workspace.rootYDoc.getMap('meta'),
      'pages'
    ).pipe(
      switchMap(pages => yjsObservePath(pages, '*.createDate')),
      map(pages => {
        if (pages instanceof YArray) {
          return pages.map(v => ({
            id: v.get('id') as string,
            createDate: (v.get('createDate') ?? 0) as number,
          }));
        } else {
          return [];
        }
      })
    );
  }

  private _yjsAllDocUpdatedDate() {
    return yjsGetPath(
      this.workspaceService.workspace.rootYDoc.getMap('meta'),
      'pages'
    ).pipe(
      switchMap(pages => yjsObservePath(pages, '*.updatedDate')),
      map(pages => {
        if (pages instanceof YArray) {
          return pages.map(v => ({
            id: v.get('id') as string,
            updatedDate: v.get('updatedDate') as number | undefined,
          }));
        } else {
          return [];
        }
      })
    );
  }

  /**
   * Intercept workspace.meta.setDocMeta() to propagate safe cache updates.
   *
   * BlockSuite also writes title changes directly while local Yjs docs hydrate.
   * Those title writes can be stale in cloud workspaces, so only controlled
   * DocsStore.setDocMeta() calls and server GraphQL refreshes update title.
   *
   * RBAC: updateDocMeta is a no-op for docs not in accessibleDocCache.
   * Only active for cloud workspaces. Installed once, no cleanup needed.
   */
  private installMetaInterceptor(): void {
    if (this.metaInterceptorInstalled || !this.isCloudWorkspace) return;
    this.metaInterceptorInstalled = true;

    const meta = this.workspaceService.workspace.docCollection.meta;
    const original = meta.setDocMeta.bind(meta);
    const cache = this.accessibleDocCache;

    meta.setDocMeta = (id: string, props: Partial<DocMeta>) => {
      original(id, props);
      // Raw BlockSuite meta writes can come from remote/hydrated Yjs state and
      // may be older than server metadata. Title cache writes for cloud docs are
      // allowed only through controlled DocsStore.setDocMeta() or GraphQL refresh.
      const cacheMeta = this.toCacheMeta(props, false);
      if (cacheMeta) cache.updateDocMeta(id, cacheMeta);
    };
  }

  private updateAccessibleDocCacheFromMeta(
    id: string,
    props: Partial<DocMeta>,
    trashRollback: {
      snapshot: ReturnType<AccessibleDocCacheStore['captureTrashSnapshot']>;
      version: number;
    } | null = null
  ): void {
    const cacheMeta = this.toCacheMeta(props, true);
    if (!cacheMeta) {
      return;
    }
    this.accessibleDocCache.updateDocMeta(id, cacheMeta, {
      persistTrashState: cacheMeta.trashed !== undefined,
    });
    if (cacheMeta.trashed !== undefined) {
      this.docMutationService
        ?.setTrashState(id, cacheMeta.trashed)
        .then(() => {
          if (
            this.docTrashMutationVersions.get(id) === trashRollback?.version
          ) {
            this.docTrashMutationVersions.delete(id);
          }
        })
        .catch(err => {
          console.error('Failed to persist doc trash mutation:', err);
          if (
            trashRollback &&
            this.docTrashMutationVersions.get(id) === trashRollback.version
          ) {
            this.accessibleDocCache.restoreTrashSnapshot(
              trashRollback.snapshot
            );
            this.docTrashMutationVersions.delete(id);
          }
        });
    }
  }

  private nextDocTrashMutationVersion(id: string): number {
    const version = (this.docTrashMutationVersions.get(id) ?? 0) + 1;
    this.docTrashMutationVersions.set(id, version);
    return version;
  }

  private toCacheMeta(
    props: Partial<DocMeta>,
    includeTitle: boolean
  ): Partial<{ title: string; trashed: boolean; updatedDate: number }> | null {
    const cacheMeta: Partial<{
      title: string;
      trashed: boolean;
      updatedDate: number;
    }> = {};
    if (includeTitle && props.title !== undefined) {
      cacheMeta.title = props.title;
    }
    if (props.trash !== undefined) cacheMeta.trashed = props.trash;
    if (props.updatedDate !== undefined) {
      cacheMeta.updatedDate = props.updatedDate;
    }
    return Object.keys(cacheMeta).length > 0 ? cacheMeta : null;
  }

  private _yjsDocMeta(id: string) {
    let docMetaIndexCache = -1;
    return yjsGetPath(
      this.workspaceService.workspace.rootYDoc.getMap('meta'),
      'pages'
    ).pipe(
      switchMap(yjsObserve),
      map(meta => {
        if (meta instanceof YArray) {
          if (docMetaIndexCache >= 0) {
            const doc = meta.get(docMetaIndexCache);
            if (doc && doc.get('id') === id) {
              return doc as YMap<any>;
            }
          }

          // meta is YArray, `for-of` is faster then `for`
          let i = 0;
          for (const doc of meta) {
            if (doc && doc.get('id') === id) {
              docMetaIndexCache = i;
              return doc as YMap<any>;
            }
            i++;
          }
          return null;
        } else {
          return null;
        }
      }),
      switchMap(yjsObserveDeep),
      map(meta => {
        if (meta instanceof YMap) {
          return meta.toJSON() as Partial<DocMeta>;
        } else {
          return {};
        }
      })
    );
  }
}
